import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import {
  drivingLicenseAllowedSelector,
  identityDocumentAllowedSelector,
  isExternalIncompleteUserSelector,
  isProfileIncompleteUserSelector,
  isRegisterIncompleteUserSelector,
  messagesSelector,
  registerCompanySelector,
  showMoreInfoPageSelector,
  userIdSelector
} from '../../helpers/selectors';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import {
  getMessage as t,
  isRMF_companyId,
  isValidId,
  navigateToHomePage
} from '../../utils/utils';
import { addErrorMsg } from '../../utils/flashMessage/creator';
import {
  autoUpdateRegisterIncompleteStatus,
  clearProfileFormData,
  getRegisterCompany,
  getUserProfiles,
  setSubscribeLoaderState
} from '../../actions/user';
import { popup_MobileAppSelector_SetVisibility } from '../../actions/popup';
import { creation as cr } from '../../constants/routes';
import { browserHistory, getCreationId } from '../../helpers/components';
import { getMemberCustomFields } from '../../actions/customFields';

function Creation(p) {
  const { pathname } = useLocation();
  const [pageIndex, setPageIndex] = useState(0);

  // Every entrant now opens on the personal-details step: Keycloak confirms the
  // e-mail address itself, so there is no activation step in front of it for anyone.
  const pageIndexMap = useMemo(() => {
    const arr = [`${cr.container}/${cr.info}/*`];

    if (p.drivingLicenseAllowed || p.identityDocumentAllowed) {
      arr.push(`${cr.container}/${cr.files}/*`);
    }
    if (p.showMoreInfoPage) {
      arr.push(`${cr.container}/${cr.moreInfo}/*`);
    }
    return arr;
  }, [p.drivingLicenseAllowed, p.identityDocumentAllowed, p.showMoreInfoPage]);

  function startFetch() {
    p.setSubscribeLoaderState(true);
  }

  function finishFetch() {
    p.setSubscribeLoaderState(false);
  }

  // Failsafe for the one RMF entry that bypasses navigateToCreationPage's guard: a
  // directly typed /register/info/<rmf-id> URL, from someone RegisterAccessGuard lets
  // through - a signed-in member, since for a visitor the guard screens RMF first and
  // this screen never mounts. Steer off the dead register route onto the homepage and
  // raise the "install the app" popup over it - no logout. The homepage rather than a
  // registration chooser: RMF has no web registration at all, so no chooser would have
  // anything to offer, and the homepage is unguarded, so the popup stays put over it.
  function handleRenaultFranceUser() {
    const id = getCreationId(p.routeParams);

    if (isRMF_companyId(id)) {
      p.popup_MobileAppSelector_SetVisibility(true, null, id);
      browserHistory.replace('/');
    }
  }

  function getCompanyDataOnLoad() {
    const id = getCreationId(p.routeParams);

    if (isRMF_companyId(id)) return;
    startFetch();

    if (isValidId(id)) {
      p.getRegisterCompany(id).then((data) => {
        if (data?.type === 'user/GET_REGISTER_COMPANY_SUCCESS') {
          p.getMemberCustomFields(id).then(finishFetch);
        } else {
          finishFetch();
          navigateToHomePage();
        }
      });
    } else {
      addErrorMsg(t(p.m, 'error.invite.code.invalid'));
      finishFetch();
      navigateToHomePage();
    }
  }

  useEffect(() => {
    if (isRMF_companyId(getCreationId(p.routeParams))) return;
    if (p.userId) p.getUserProfiles();
  }, [p.userId]);

  useEffect(() => {
    setPageIndex(pageIndexMap.findIndex((route) => matchPath(route, pathname)));
  }, [pathname, pageIndexMap]);

  useEffect(() => {
    handleRenaultFranceUser();
    getCompanyDataOnLoad();
    // Nothing about the registration is ended here any more. This screen unmounts on a
    // plain Back to the company picker too - one step inside the same flow - and
    // clearing anything there told the registration guard the visitor had no business
    // on the screen he was going back to. RegisterAccessGuard spans the whole flow and
    // reports the one departure that means he left it.
    return () => {
      p.clearProfileFormData();
      p.autoUpdateRegisterIncompleteStatus();
      finishFetch();
    };
  }, []);

  function getTitlePart() {
    // Whose registration is on screen. The three audiences exclude one another by
    // construction: the external flag carries a visitor until /me answers; a readable
    // TO_COMPLETE/APPLIED member then raises isRegisterIncompleteUser; and the profile
    // selector can only be true while both of those flags are false.
    let titlePart = 'part.create.account.for';

    if (p.isProfileIncompleteUser) titlePart = 'part.link.account.to';
    // One audience under two flags: a Keycloak visitor stays external-incomplete until
    // /me returns a readable member, then that member's own unfinished status carries
    // the same heading over the remaining steps. A registration response alone does
    // not move the heading because it is not a readable-member verdict.
    if (p.isExternalIncompleteUser || p.isRegisterIncompleteUser) {
      titlePart = 'part.complete.register.for';
    }
    return (
      <div className="scope-title">
        <FormattedMessage id={titlePart} />
      </div>
    );
  }

  function renderCompanyName() {
    return p?.company?.name || <FormattedMessage id="generic.company" />;
  }

  function renderSteps() {
    const steps = [];

    for (let i = 0; i < pageIndexMap.length; i++) {
      function getActiveIndex() {
        return i === pageIndex;
      }

      function renderStepNumber() {
        return (
          <div
            key={i}
            className={classnames('scope-step', {
              'sub-class-active': getActiveIndex()
            })}
          >
            {i + 1}
          </div>
        );
      }

      function renderStepLine() {
        if (i < pageIndexMap.length - 1) {
          return <div key={i + '.sub'} className="scope-step-line" />;
        }
      }

      steps.push(renderStepNumber());
      steps.push(renderStepLine());
    }
    return steps;
  }

  return (
    <div className={classnames('creation-page', 'register-page', 'profile-page')}>
      <div className={classnames('scope-page-header')}>
        <div className="scope-title-container">
          {getTitlePart()}
          <div className="scope-company-name">{renderCompanyName()}</div>
        </div>

        <div className="scope-steps-container">{renderSteps()}</div>
      </div>
      <div className={classnames('scope-content', 'active')}>
        <Outlet />
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    m: messagesSelector(state),
    company: registerCompanySelector(state),
    userId: userIdSelector(state),
    isRegisterIncompleteUser: isRegisterIncompleteUserSelector(state),
    isProfileIncompleteUser: isProfileIncompleteUserSelector(state),
    isExternalIncompleteUser: isExternalIncompleteUserSelector(state),
    drivingLicenseAllowed: drivingLicenseAllowedSelector(state),
    identityDocumentAllowed: identityDocumentAllowedSelector(state),
    showMoreInfoPage: showMoreInfoPageSelector(state)
  };
}

const mapDispatchToProps = {
  getUserProfiles,
  setSubscribeLoaderState,
  getRegisterCompany,
  autoUpdateRegisterIncompleteStatus,
  getMemberCustomFields,
  clearProfileFormData,
  popup_MobileAppSelector_SetVisibility
};

Creation = connect(mapStateToProps, mapDispatchToProps)(Creation);

export default Creation;
